/**
 * The offline / pending-sync bar.
 *
 * Plain DOM with inline styles, like capacitor/offline.js's banner and for the
 * same reason: it must work when the network died during the initial load and
 * React never mounted. It replaces that banner when offline logging is on (the
 * banner only ever said "You're offline", which is the first line of this one),
 * and unlike it, this runs in a mobile browser too, not only in the app.
 *
 * What it says, in priority order:
 *   failures   "2 items couldn't sync."  [Retry] [Discard]
 *   offline    "Offline. 3 sets and 1 video saved on this phone."
 *   syncing    "Syncing 3 sets and 1 video... 40%"
 *   done       "All synced" (briefly)
 *   otherwise  hidden
 */

import {
  discardFailedOfflineWork,
  retryFailedOfflineWork,
  subscribeOfflineSyncStatus,
} from "./sync_engine";

export const INDICATOR_ID = "tkc-offline-sync-status";
const DONE_VISIBLE_MS = 2500;

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const describeQueued = ({ pendingSets, pendingVideos }) => {
  const parts = [];
  if (pendingSets > 0) parts.push(plural(pendingSets, "set", "sets"));
  if (pendingVideos > 0) parts.push(plural(pendingVideos, "video", "videos"));
  return parts.join(" and ");
};

/** Pure: status -> { text, tone, actions } or null when hidden. */
export const indicatorContent = (status) => {
  const failed = status.failedSets + status.failedVideos;
  const queued = describeQueued(status);

  if (failed > 0) {
    return {
      tone: "error",
      text: `${plural(failed, "item", "items")} couldn't sync.`,
      actions: ["retry", "discard"],
    };
  }
  if (!status.online) {
    return {
      tone: "offline",
      text: queued
        ? `Offline. ${queued} saved on this phone, will sync when you reconnect.`
        : "Offline. Sets and videos you log are saved on this phone.",
      actions: [],
    };
  }
  if (status.syncing || queued) {
    const progress =
      status.videoProgress != null ? ` ${status.videoProgress}%` : "";
    return {
      tone: "syncing",
      text: queued
        ? `Syncing ${queued}...${progress}`
        : `Syncing...${progress}`,
      actions: [],
    };
  }
  if (status.justSynced) {
    return { tone: "done", text: "All synced", actions: [] };
  }
  return null;
};

const TONE_BACKGROUND = {
  offline: "#323232",
  syncing: "#0054a4", // TKC brand blue, same as the native notification tint
  done: "#2e7d32",
  error: "#b3261e",
};

const buttonStyle = [
  "margin-left:10px",
  "padding:4px 10px",
  "border:1px solid rgba(255,255,255,0.7)",
  "border-radius:14px",
  "background:transparent",
  "color:#fff",
  "font:600 13px/1.2 inherit",
  "cursor:pointer",
].join(";");

function ensureElement() {
  let el = document.getElementById(INDICATOR_ID);
  if (el) return el;
  el = document.createElement("div");
  el.id = INDICATOR_ID;
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.style.cssText = [
    "position:fixed",
    "left:0",
    "right:0",
    "bottom:0",
    "z-index:2147483647",
    "display:none",
    "padding:10px 12px calc(10px + env(safe-area-inset-bottom))",
    "text-align:center",
    "color:#fff",
    "font:600 14px/1.3 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    "box-shadow:0 -1px 6px rgba(0,0,0,0.25)",
  ].join(";");
  document.body.appendChild(el);
  return el;
}

let discardArmed = false;

function render(el, content) {
  if (!content) {
    el.style.display = "none";
    return;
  }
  el.style.display = "block";
  el.style.background = TONE_BACKGROUND[content.tone];
  el.textContent = content.text;

  if (content.actions.includes("retry")) {
    const retry = document.createElement("button");
    retry.type = "button";
    retry.textContent = "Retry";
    retry.style.cssText = buttonStyle;
    retry.addEventListener("click", () => {
      discardArmed = false;
      retryFailedOfflineWork();
    });
    el.appendChild(retry);
  }
  if (content.actions.includes("discard")) {
    // Two taps: this throws away sets or a video the client logged, and there
    // is no undo. No browser confirm() (it blocks the WebView).
    const discard = document.createElement("button");
    discard.type = "button";
    discard.textContent = discardArmed ? "Tap again to discard" : "Discard";
    discard.style.cssText = buttonStyle;
    discard.addEventListener("click", () => {
      if (!discardArmed) {
        discardArmed = true;
        render(el, content);
        return;
      }
      discardArmed = false;
      discardFailedOfflineWork();
    });
    el.appendChild(discard);
  }
}

let mounted = false;

export function mountOfflineIndicator() {
  if (mounted || typeof document === "undefined") return;
  mounted = true;
  let hideTimer = null;
  subscribeOfflineSyncStatus((status) => {
    const el = ensureElement();
    clearTimeout(hideTimer);
    const content = indicatorContent(status);
    render(el, content);
    if (content?.tone === "done") {
      hideTimer = setTimeout(() => render(el, null), DONE_VISIBLE_MS);
    }
  });
}

export const resetOfflineIndicatorForTests = () => {
  mounted = false;
  discardArmed = false;
  document.getElementById(INDICATOR_ID)?.remove();
};
